import { useEffect, useState } from 'react';
import { css } from '@emotion/react';

import { makeStyles } from '@toolbox/style';
import { Step, useLeadData, useWidgetContext } from '@widget/core';
import {
  Box,
  Container,
  Grid,
  GridItem,
  LoadingAnimatedIcon,
  LoadingAnimatedText,
} from '@toolbox/components';
import { QuestionHeader } from '@widget/components';
import { CheckAnimatedIcon } from '@widget/components/CheckAnimatedIcon';

import type { ReactNode } from 'react';
import type { StepProps } from '@widget/core';

export type ValuationStepProps = Omit<StepProps, 'children'> & {
  title: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  closeButtonText?: string;
  loadingText?: string | null;
};

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(4)};
  `,
  animation: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 180px;
    height: 180px;
    margin: ${theme.spacing(10)} auto;
  `,
}));

export const ValuationStep = ({
  title,
  id,
  icon,
  nextStep,
  closeButtonText,
  loadingText,
  ...stepProps
}: ValuationStepProps): JSX.Element => {
  const classes = useStyles();
  const widgetContext = useWidgetContext();
  const { loadingLead, leadData } = useLeadData();

  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!loadingLead && !widgetContext.allStepsPreviewMode) {
      const isDirectAppointmentBooking = widgetContext.expectRenderWhen?.(
        { isFirstBrokerBookingStep: true },
        widgetContext.getFormData(),
      );

      if (isDirectAppointmentBooking) {
        // to skip checkmark animation, when trying to load slots
        widgetContext.commit(id, { isNewCustomer: leadData?.isNewCustomer }, nextStep);
      } else {
        setSuccess(true);
      }
    }
  }, [widgetContext.allStepsPreviewMode, loadingLead]);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        widgetContext.commit(id, { isNewCustomer: leadData?.isNewCustomer }, nextStep);
      }, 1500);
    }
  }, [success, widgetContext]);

  return (
    <Step id={id} {...stepProps}>
      <Container css={classes.root as any}>
        <Grid>
          <GridItem xs={12}>
            <QuestionHeader closeButtonText={closeButtonText} title={title} />
            {success ? (
              <Box css={classes.animation as any}>
                <CheckAnimatedIcon />
              </Box>
            ) : (
              <Box css={classes.animation as any}>
                <LoadingAnimatedIcon />
                <Box mt={3}>
                  <LoadingAnimatedText text={loadingText} />
                </Box>
              </Box>
            )}
          </GridItem>
        </Grid>
      </Container>
    </Step>
  );
};
